import { useState } from 'react'
import type { FormEvent } from 'react'
import {
  useAdmins,
  useCreateAdminAccount,
  useDeactivateAdmin,
  useResetAdminPassword,
} from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { SelectField, TextField } from '@/components/ui/Field'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { formatDateTime } from '@/lib/format'
import type {
  AdminAccount,
  AdminRole,
  CreatedAdminAccount,
} from '@/types/api'
import styles from './AdminAccountsPage.module.css'

/** Matches the server's rule, which every other password field also uses. */
const MIN_PASSWORD = 8

/**
 * Admin accounts.
 *
 * *** AN INSTITUTE HAS MORE THAN ONE ADMIN ***
 * Until this page existed there was no way to see who else could sign in, and
 * a colleague who forgot their password had to wait for the platform team.
 *
 * *** SETTING A COLLEAGUE'S PASSWORD MEANS KNOWING IT ***
 * Whoever performs a reset can then sign in as that person. That is a
 * deliberate decision for a single institute, where admins already share full
 * control of bookings, money and students — but it is said plainly on the
 * page, because an admin should understand what they are doing rather than
 * discover it later. Every reset is recorded in the audit log with who did it.
 */
export function AdminAccountsPage() {
  const toast = useToast()
  const { data, isLoading, error, refetch } = useAdmins()
  const resetMutation = useResetAdminPassword()
  const createMutation = useCreateAdminAccount()
  const deactivateMutation = useDeactivateAdmin()

  const [resetting, setResetting] = useState<AdminAccount | null>(null)
  const [creating, setCreating] = useState(false)

  /** Shown once after creation. Never fetchable again — only a hash is kept. */
  const [created, setCreated] = useState<CreatedAdminAccount | null>(null)

  /** Controlled so the description under the picker tracks the choice. */
  const [role, setRole] = useState<AdminRole>('COORDINATOR')

  /** Whether the admin typed the password, or left it to be generated. */
  const [chosePassword, setChosePassword] = useState(false)

  /** The colleague being removed, pending confirmation. */
  const [removing, setRemoving] = useState<AdminAccount | null>(null)

  const admins = data?.admins ?? []
  const locked = admins.filter((a) => a.is_locked)

  const openCreate = () => {
    // Reset before opening: the dialog stays mounted, so a level left from
    // last time would be shown as though it belonged to this colleague.
    setRole('COORDINATOR')
    setCreating(true)
  }

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const typed = String(form.get('password') ?? '').trim()
    setChosePassword(typed.length > 0)

    try {
      const result = await createMutation.mutateAsync({
        email: String(form.get('email') ?? '').trim(),
        full_name: String(form.get('full_name') ?? '').trim(),
        admin_role: role,
        // Empty means "generate one" — the server decides, so an admin who
        // leaves it blank still gets a usable account.
        password: typed || null,
      })
      setCreating(false)
      // Straight into the password dialog: dismissed unread, the only way back
      // is a reset.
      setCreated(result)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not create the account.',
      )
    }
  }

  const handleDeactivate = async () => {
    if (!removing) return
    try {
      await deactivateMutation.mutateAsync(removing.user_id)
      toast.success(`${removing.email ?? 'That account'} can no longer sign in.`)
      setRemoving(null)
    } catch (caught) {
      // The server refuses the last administrator and self-removal, and its
      // wording explains which — better than anything guessable here.
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not deactivate the account.',
      )
    }
  }

  const handleReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!resetting) return

    const form = new FormData(event.currentTarget)
    const password = String(form.get('new_password') ?? '')
    const confirm = String(form.get('confirm_password') ?? '')

    // Caught here so the admin reads a question rather than a 422 — and
    // because a typo would otherwise lock the colleague out completely,
    // with nobody knowing the password that was actually set.
    if (password.length < MIN_PASSWORD) {
      toast.error(`Use at least ${MIN_PASSWORD} characters.`)
      return
    }
    if (password !== confirm) {
      toast.error('The two passwords do not match.')
      return
    }

    try {
      await resetMutation.mutateAsync({
        userId: resetting.user_id,
        newPassword: password,
      })
      toast.success(
        resetting.is_you
          ? 'Your password has been changed.'
          : `Password set. Tell ${resetting.email ?? 'them'} what it is.`,
      )
      setResetting(null)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not set the password.',
      )
    }
  }

  const columns: Array<Column<AdminAccount>> = [
    {
      key: 'email',
      header: 'Admin',
      render: (row) => (
        <div>
          <span className={styles.email}>{row.email ?? 'No email'}</span>
          {row.is_you && <span className={styles.youTag}>you</span>}
          <div className={styles.muted}>
            {row.created_at
              ? `Added ${formatDateTime(row.created_at)}`
              : 'Added date unknown'}
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Access',
      render: (row) => (
        <div>
          {/* NULL predates roles and means full access — said plainly rather
              than left as a blank cell that reads like missing data. */}
          <Pill
            tone={
              !row.admin_role || row.admin_role === 'ADMIN'
                ? 'success'
                : 'neutral'
            }
          >
            {ROLE_LABELS[row.admin_role ?? 'ADMIN']}
          </Pill>
          <div className={styles.muted}>
            {ROLE_SUMMARIES[row.admin_role ?? 'ADMIN']}
          </div>
        </div>
      ),
    },
    {
      key: 'last_login',
      header: 'Last signed in',
      render: (row) =>
        row.last_login_at ? (
          <span>{formatDateTime(row.last_login_at)}</span>
        ) : (
          // Worth saying: an account that has never been used may be one
          // whose password was never passed on.
          <span className={styles.muted}>Never</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.is_locked ? (
          // The usual reason someone asks for a reset, so it leads.
          <Pill tone="danger">Locked out</Pill>
        ) : row.status === 'ACTIVE' ? (
          <Pill tone="success">Active</Pill>
        ) : (
          <Pill tone="neutral">{row.status}</Pill>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '240px',
      render: (row) => (
        <div className={styles.rowActions}>
          <Button size="sm" variant="ghost" onClick={() => setResetting(row)}>
            Set password
          </Button>
          {/* Never on your own row: the server refuses it, and offering a
              button that always errors is worse than not offering it. */}
          {!row.is_you && (
            <Button size="sm" variant="ghost" onClick={() => setRemoving(row)}>
              Remove
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Admin accounts"
        description="Everyone who can sign in to this portal, and what each of them can see."
        actions={
          <Button variant="primary" onClick={openCreate}>
            Add colleague
          </Button>
        }
      />

      {locked.length > 0 && (
        <Callout tone="warning" title="Somebody is locked out">
          {locked.length === 1
            ? `${locked[0]?.email ?? 'An admin'} is locked out after too many failed sign-ins.`
            : `${locked.length} admins are locked out after too many failed sign-ins.`}{' '}
          Setting a new password clears the lock.
        </Callout>
      )}

      <Card>
        <Table
          columns={columns}
          rows={admins}
          rowKey={(row) => row.user_id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          emptyMessage="No admin accounts."
        />
      </Card>

      {/*
        *** SAID PLAINLY, NOT BURIED ***
        An admin should know that setting a colleague's password means holding
        a working credential for that account — before they do it, not after.
      */}
      <Callout tone="info" title="About setting passwords">
        You choose the new password and tell your colleague what it is. Until
        they change it themselves, you also know how to sign in as them, so
        every reset is recorded against your name.
      </Callout>

      {/* ---------------------------------------------------- add colleague */}
      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Add a colleague"
      >
        <form onSubmit={handleCreate}>
          <div className={styles.dialogBody}>
            <TextField
              label="Email"
              name="email"
              type="email"
              required
              hint="They sign in with this."
            />
            <TextField
              label="Full name"
              name="full_name"
              required
              maxLength={200}
            />

            <div>
              <SelectField
                label="Access level"
                name="admin_role"
                value={role}
                onChange={(event) => setRole(event.target.value as AdminRole)}
                options={[
                  { value: 'COORDINATOR', label: 'Coordinator' },
                  { value: 'ACCOUNTANT', label: 'Accountant' },
                ]}
                hint="Another full administrator can only be added by the platform team."
              />
              {/* The consequence of the choice, in the same breath as making
                  it — rather than a label the reader has to interpret. */}
              <p className={styles.roleHint}>{ROLE_DETAILS[role]}</p>
            </div>

            {/*
              *** A GENERATED PASSWORD CANNOT BE READ ACROSS A DESK ***
              `xK9-mQ2vLpT` has to be spelled out letter by letter, and the
              l/1 or O/0 goes wrong often enough to need a second reset. An
              admin sitting with their new colleague wants to say it once.
              Left blank, the server generates one as before.
            */}
            <TextField
              label="Password"
              name="password"
              type="text"
              minLength={8}
              maxLength={200}
              hint="At least 8 characters. Leave blank to have one generated for you."
            />
          </div>

          <div className={styles.formActions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreating(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMutation.isPending}
            >
              Create account
            </Button>
          </div>
        </form>
      </Modal>

      {/* ------------------------------------------- the one-time password */}
      <Modal
        open={created !== null}
        onClose={() => setCreated(null)}
        title="Account created"
      >
        <div className={styles.dialogBody}>
          <p className={styles.muted}>
            Give {created?.admin.email} this password.
            {!chosePassword
              ? ' They will be asked to change it when they first sign in.'
              : ' They can sign in with it straight away.'}
          </p>

          <div className={styles.passwordBox}>{created?.temporary_password}</div>

          {/*
            *** ONLY WHEN THE SERVER GENERATED IT ***
            If the admin typed the password they already know it, and telling
            them it can never be retrieved is noise — the kind that teaches
            people to dismiss warnings that do matter.
          */}
          {!chosePassword && (
            <Callout tone="warning" title="This is shown only once">
              Only a scrambled copy is stored, so it cannot be looked up again.
              If it is lost, set a new password from this page.
            </Callout>
          )}
        </div>

        <div className={styles.formActions}>
          <Button variant="primary" onClick={() => setCreated(null)}>
            Done
          </Button>
        </div>
      </Modal>

      {/* ------------------------------------------------ remove a colleague */}
      <Modal
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title="Remove access"
      >
        <div className={styles.dialogBody}>
          <p className={styles.muted}>
            {removing?.email ?? 'This account'} will no longer be able to sign
            in to this portal.
          </p>

          {/*
            *** SAID PLAINLY, BECAUSE "DELETE" USUALLY MEANS GONE ***
            Everything they did stays attributable. An admin who expects a
            hard delete should know it is not one.
          */}
          <Callout tone="info" title="Their history is kept">
            Sessions, payments and approvals they handled stay on record with
            their name, so past decisions remain explainable.
          </Callout>
        </div>

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setRemoving(null)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={deactivateMutation.isPending}
            onClick={handleDeactivate}
          >
            Remove access
          </Button>
        </div>
      </Modal>

      <Modal
        open={resetting !== null}
        onClose={() => setResetting(null)}
        title={resetting?.is_you ? 'Change your password' : 'Set a new password'}
      >
        <form onSubmit={handleReset}>
          <div className={styles.dialogBody}>
            <div className={styles.target}>
              <span className={styles.targetLabel}>
                {resetting?.is_you ? 'Your account' : 'For'}
              </span>
              <span className={styles.targetValue}>
                {resetting?.email ?? 'this account'}
              </span>
            </div>

            <TextField
              label="New password"
              name="new_password"
              type="password"
              required
              minLength={MIN_PASSWORD}
              maxLength={200}
              autoComplete="new-password"
              hint={`At least ${MIN_PASSWORD} characters. Pick something they can remember.`}
            />

            {/*
              Typed twice because nobody can read it back. The server stores
              only a hash, so a typo would lock the colleague out with a
              password neither of you knows.
            */}
            <TextField
              label="Type it again"
              name="confirm_password"
              type="password"
              required
              minLength={MIN_PASSWORD}
              maxLength={200}
              autoComplete="new-password"
            />

            {resetting && !resetting.is_you && (
              <p className={styles.muted}>
                {resetting.email ?? 'They'} can sign in with this straight
                away. It replaces their old password immediately, so tell them
                before they next try.
              </p>
            )}
          </div>

          <div className={styles.formActions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setResetting(null)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={resetMutation.isPending}
            >
              Set password
            </Button>
          </div>
        </form>
      </Modal>
    </PageStack>
  )
}

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrator',
  COORDINATOR: 'Coordinator',
  ACCOUNTANT: 'Accountant',
}

const ROLE_SUMMARIES: Record<string, string> = {
  ADMIN: 'Everything',
  COORDINATOR: 'Bookings, students, mentors',
  ACCOUNTANT: 'Payments and plans',
}

/** Kept in step with ADMIN_AREA_GRANTS on the server. */
const ROLE_DETAILS: Record<AdminRole, string> = {
  ADMIN:
    'Full access, including admin accounts, settings, and everything below.',
  COORDINATOR:
    'Bookings, students, mentors, time off, feedback and messages. Cannot see payments, change prices, or alter settings.',
  ACCOUNTANT:
    'Payments, cash requests, subscription plans, fee heads and pricing, plus bookings for reconciliation. Cannot see student or mentor records.',
}
